import { useCallback, useMemo, useRef, useState } from 'react';
import { nanoid } from 'nanoid';
// Keeps unknown step keys so they round-trip; drops only a stray `template`
// (templates live in MarkTemplate rows) and null optionals the BE omits.
const toStructuralStep = (step) => {
    const out = { ...step };
    delete out.template;
    if (out.exit_reason == null)
        delete out.exit_reason;
    if (out.type === 'check_segment' && out.segment_id == null)
        delete out.segment_id;
    return out;
};
const cloneState = (version) => ({
    steps: JSON.parse(JSON.stringify(version.steps)).map(toStructuralStep),
    entryStepId: version.entry_step_id,
    sendWindowStart: version.send_window_start,
    sendWindowEnd: version.send_window_end
});
const makeEmptyState = () => ({
    steps: [],
    entryStepId: null,
    sendWindowStart: null,
    sendWindowEnd: null
});
const isSameState = (a, b) => JSON.stringify(a.steps) === JSON.stringify(b.steps) &&
    a.entryStepId === b.entryStepId &&
    a.sendWindowStart === b.sendWindowStart &&
    a.sendWindowEnd === b.sendWindowEnd;
const toBase = (drip) => ({
    status: drip.status,
    draftVersionNumber: drip.draft_version?.version_number ?? null
});
const useDripDraft = (drip) => {
    const sourceVersion = drip?.draft_version ?? drip?.current_version ?? null;
    const sourceState = sourceVersion ? cloneState(sourceVersion) : makeEmptyState();
    const hydrationKey = drip
        ? `${drip.drip_id}:${sourceVersion?.version_number ?? 0}:${JSON.stringify(sourceState)}`
        : null;
    const [state, setState] = useState(makeEmptyState);
    const [pending, setPending] = useState(null);
    const snapshot = useRef(makeEmptyState());
    const base = useRef(null);
    const hydratedKey = useRef(null);
    const hydratedDripId = useRef(null);
    if (drip && hydrationKey !== null && hydrationKey !== hydratedKey.current) {
        hydratedKey.current = hydrationKey;
        const fresh = sourceState;
        const localDirty = !isSameState(state, snapshot.current);
        // Refreshed content for the same drip while the user has unsaved edits
        // that don't match it (i.e. not our own save round-tripping): keep the
        // edits and surface the newer version instead of clobbering them.
        if (hydratedDripId.current === drip.drip_id && localDirty && !isSameState(state, fresh)) {
            setPending({
                versionNumber: sourceVersion?.version_number ?? 0,
                state: fresh,
                base: toBase(drip)
            });
        }
        else {
            snapshot.current = fresh;
            base.current = toBase(drip);
            setState(fresh);
            setPending(null);
        }
        hydratedDripId.current = drip.drip_id;
    }
    const persistedSteps = useMemo(() => new Map(snapshot.current.steps.map((s) => [s.id, s])), [snapshot.current.steps]);
    const persistedStepIds = useMemo(() => new Set(persistedSteps.keys()), [persistedSteps]);
    const dirty = !isSameState(state, snapshot.current);
    const matchesPersisted = useCallback((latest) => {
        const version = latest.draft_version ?? latest.current_version;
        return isSameState(version ? cloneState(version) : makeEmptyState(), snapshot.current);
    }, []);
    const stageLatest = useCallback((latest) => {
        const version = latest.draft_version ?? latest.current_version;
        setPending({
            versionNumber: version?.version_number ?? 0,
            state: version ? cloneState(version) : makeEmptyState(),
            base: toBase(latest)
        });
    }, []);
    // Status can change without a new version (pause/resume); follow it while
    // there is nothing local to protect.
    if (drip && !dirty && pending == null)
        base.current = toBase(drip);
    const setSteps = useCallback((steps) => {
        setState((prev) => ({ ...prev, steps }));
    }, []);
    const setEntryStepId = useCallback((id) => {
        setState((prev) => ({ ...prev, entryStepId: id }));
    }, []);
    const setSendWindow = useCallback((start, end) => {
        setState((prev) => ({ ...prev, sendWindowStart: start, sendWindowEnd: end }));
    }, []);
    const addStep = useCallback((type, activity) => {
        const id = nanoid(8).toLowerCase();
        let step;
        switch (type) {
            case 'check_segment':
                step = {
                    id,
                    type: 'check_segment',
                    wait_for: null,
                    on_true: 'exit',
                    on_false: 'exit',
                    exit_reason: 'SEGMENT_EXIT'
                };
                break;
            case 'activity':
                step = {
                    id,
                    type: 'activity',
                    activity: activity ?? 'send_email',
                    next: 'exit',
                    exit_reason: 'COMPLETED'
                };
                break;
            case 'ab_split':
                step = {
                    id,
                    type: 'ab_split',
                    ratio: 50,
                    arms: { A: 'exit', B: 'exit' },
                    exit_reason: 'COMPLETED'
                };
                break;
        }
        setState((prev) => {
            const newSteps = [...prev.steps, step];
            const newEntry = prev.steps.length === 0 ? id : prev.entryStepId;
            return { ...prev, steps: newSteps, entryStepId: newEntry };
        });
        return id;
    }, []);
    const updateStep = useCallback((id, patch) => {
        setState((prev) => ({
            ...prev,
            steps: prev.steps.map((s) => (s.id === id ? { ...s, ...patch } : s))
        }));
    }, []);
    const removeStep = useCallback((id) => {
        setState((prev) => {
            const newSteps = prev.steps.filter((s) => s.id !== id);
            const newEntry = prev.entryStepId === id ? (newSteps[0]?.id ?? null) : prev.entryStepId;
            return { ...prev, steps: newSteps, entryStepId: newEntry };
        });
    }, []);
    const discard = useCallback(() => {
        if (pending) {
            snapshot.current = pending.state;
            base.current = pending.base;
            setPending(null);
            setState(pending.state);
            return;
        }
        setState(snapshot.current);
    }, [pending]);
    const buildUpdateBody = useCallback((dripId) => ({
        drip_id: dripId,
        steps: state.steps.map(toStructuralStep),
        entry_step_id: state.entryStepId,
        send_window_start: state.sendWindowStart,
        send_window_end: state.sendWindowEnd
    }), [state]);
    const markClean = useCallback((version) => {
        const fresh = cloneState(version);
        snapshot.current = fresh;
        setPending(null);
        setState(fresh);
    }, []);
    return {
        state,
        dirty,
        base: base.current,
        newerVersionNumber: pending?.versionNumber ?? null,
        matchesPersisted,
        stageLatest,
        persistedStepIds,
        persistedSteps,
        setSteps,
        setEntryStepId,
        setSendWindow,
        addStep,
        updateStep,
        removeStep,
        discard,
        buildUpdateBody,
        markClean
    };
};
export default useDripDraft;
